import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Boxes, CheckCircle2, ChevronRight, Clock3,
  CircleDollarSign, FileText, Minus, PackageCheck, Plus, RefreshCw, Route, Truck, Weight,
} from 'lucide-react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { ALERTAS_DASHBOARD } from '@/graphql/trazabilidadAlertas.gql.js';
import { fetchDashboardCargas } from '@/services/dashboards.js';
import { useDashboardResource } from '@/hooks/useDashboardResource.js';
import {
  formatCompactCurrency, formatCurrencyARS, formatDuration, formatNumber,
} from './dashboard/formatters.js';
import { CargasPage, CargasSection, cargasButtonClassName } from './components/CargasLayout.jsx';

const REFRESH_MS = 60_000;
// Each action needs the permission of the screen it opens; a denied route would bounce back here.
const QUICK_ACTIONS = [
  { label: 'Nueva guía', icon: Plus, to: '/guias/emitir', permission: 'GUIAS_EMITIR' },
  { label: 'Nueva foja', icon: Plus, to: '/fojas/emision', permission: 'FOJAS_CREAR' },
  { label: 'Nuevo reparto', icon: Plus, to: '/repartos/nuevo', permission: 'REPARTOS_CREAR' },
  { label: 'Confirmar despachos', icon: Truck, to: '/cargas/confirmacion-de-despachos', permission: 'FOJAS_CONFIRMAR_DESPACHO' },
  { label: 'Recepción', icon: PackageCheck, to: '/cargas/recepcion', permission: 'RECEPCION_VER' },
];
const SECTION_LABELS = {
  sucursal: 'datos de la sucursal',
  kpis: 'indicadores del día',
  porRecibir: 'guías en camino',
  lineas: 'carga por línea',
  capacidades: 'capacidad de líneas',
  repartos: 'repartos del día',
  repartosAnteriores: 'repartos anteriores',
  entregas: 'entregas',
  colaReparto: 'cola de reparto',
  retiros: 'retiros',
  fojas: 'fojas sin salida',
  destinos: 'destinos',
  actividad: 'ritmo de emisión',
};
const STALE_LINE_HOURS = 24;
// Matches LINE_READY_THRESHOLD in the cargas dashboard API: a line at this
// occupancy is ready to dispatch.
const LINE_READY_PCT = 85;

const plural = (count, singular, pluralForm) => `${formatNumber(count)} ${count === 1 ? singular : pluralForm}`;
const timeLabel = (date) => (date ? new Intl.DateTimeFormat('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date) : '');

function Region({ title, className = '', children }) {
  return <div role="region" aria-label={title} className={`min-w-0 ${className}`}>{children}</div>;
}

function Unavailable() {
  return (
    <p className="flex items-center gap-2 rounded-xl border border-dashed border-[rgba(var(--border),0.7)] px-3 py-4 text-xs text-[rgba(var(--muted),0.95)]">
      <AlertTriangle size={14} className="text-[rgb(var(--warning))]" />
      No se pudo obtener esta sección.
    </p>
  );
}

function Empty({ children }) {
  return <p className="px-2 py-4 text-center text-xs text-[rgba(var(--muted),0.9)]">{children}</p>;
}

function DeltaBadge({ deltaPct }) {
  if (deltaPct === null || deltaPct === undefined) {
    return <span className="text-[11px] text-[rgba(var(--muted),0.85)]">Sin base de comparación</span>;
  }
  const Icon = deltaPct > 0 ? ArrowUpRight : deltaPct < 0 ? ArrowDownRight : Minus;
  const tone = deltaPct > 0 ? 'text-[rgb(var(--success))]' : deltaPct < 0 ? 'text-[rgb(var(--warning))]' : 'text-[rgba(var(--muted),0.85)]';
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[rgba(var(--fg),0.72)]">
      <Icon size={13} className={tone} aria-hidden="true" />
      <span>{`${deltaPct > 0 ? '+' : ''}${deltaPct}% vs. ayer a esta hora`}</span>
    </span>
  );
}

function KpiCard({ testId, icon, label, value, title, footer, unavailable = false }) {
  const Icon = icon;
  return (
    <article
      data-testid={testId}
      title={unavailable ? undefined : title}
      className="flex min-w-0 flex-col gap-1 rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgb(var(--panel))] px-4 py-3 shadow-[0_2px_8px_rgba(var(--shadow),0.10)]"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[10px] font-semibold uppercase tracking-widest text-[rgba(var(--muted),0.9)]">{label}</span>
        <Icon size={16} className="shrink-0 text-[rgb(var(--brand))] opacity-80" aria-hidden="true" />
      </div>
      <p className="truncate text-2xl font-bold leading-tight text-[rgb(var(--fg))]">{unavailable ? '—' : value}</p>
      <div className="min-h-[16px] truncate">{unavailable ? <span className="text-[11px] text-[rgba(var(--muted),0.9)]">Dato no disponible</span> : footer}</div>
    </article>
  );
}

const FOCUS_TONES = {
  critical: 'border-[rgba(var(--danger),0.35)] bg-[rgba(var(--danger),0.08)]',
  warning: 'border-[rgba(var(--warning),0.35)] bg-[rgba(var(--warning),0.08)]',
  info: 'border-[rgba(var(--brand),0.30)] bg-[rgba(var(--brand),0.07)]',
};

function FocusStrip({ items, onNavigate }) {
  return (
    <Region title="Foco del día">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.9)] px-3 py-2">
        <span className="mr-1 text-[10px] font-bold uppercase tracking-widest text-[rgba(var(--muted),0.95)]">Foco del día</span>
        {items.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[rgb(var(--fg))]">
            <CheckCircle2 size={15} className="text-[rgb(var(--success))]" aria-hidden="true" />
            Operación al día: no hay pendientes críticos.
          </span>
        ) : items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onNavigate(item.to)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold text-[rgb(var(--fg))] transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${FOCUS_TONES[item.tone]}`}
          >
            {item.tone === 'critical'
              ? <AlertTriangle size={13} className="text-[rgb(var(--danger))]" aria-hidden="true" />
              : <Clock3 size={13} className="text-[rgb(var(--warning))]" aria-hidden="true" />}
            {item.label}
            <ChevronRight size={13} aria-hidden="true" />
          </button>
        ))}
      </div>
    </Region>
  );
}

const LINE_STATUS = {
  lista: { label: 'Lista para despachar', className: 'bg-[rgba(var(--success),0.14)] text-[rgb(var(--fg))]', bar: 'rgb(var(--success))' },
  cargando: { label: 'Cargando', className: 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]', bar: 'rgb(var(--brand))' },
  sin_limite: { label: 'Sin capacidad cargada', className: 'bg-[rgba(var(--muted),0.14)] text-[rgba(var(--fg),0.75)]', bar: 'rgba(var(--muted),0.6)' },
};

function LineRow({ linea, maxKg, onOpen }) {
  const status = LINE_STATUS[linea.estado] || LINE_STATUS.sin_limite;
  const width = linea.ocupacionPct !== null ? Math.min(linea.ocupacionPct, 100) : Math.round((linea.kg / maxKg) * 100);
  const stale = linea.antiguedadHoras >= STALE_LINE_HOURS;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group w-full rounded-xl border border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.35)] px-3 py-2.5 text-left transition hover:border-[rgba(var(--brand),0.35)] hover:bg-[rgba(var(--brand),0.05)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-[rgb(var(--fg))]">{linea.nombre}</span>
          {linea.nombre !== linea.linea && linea.linea !== 'SIN_LINEA' ? (
            <span className="rounded bg-[rgba(var(--muted),0.12)] px-1.5 py-0.5 font-mono text-[10px] text-[rgba(var(--fg),0.6)]">{linea.linea}</span>
          ) : null}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${status.className}`}>{status.label}</span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[11px] ${stale ? 'font-semibold text-[rgb(var(--fg))]' : 'text-[rgba(var(--muted),0.95)]'}`}>
          {stale ? <AlertTriangle size={12} className="text-[rgb(var(--warning))]" aria-hidden="true" /> : <Clock3 size={12} aria-hidden="true" />}
          {`Guía más antigua: ${formatDuration(linea.antiguedadHoras * 60)}`}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div
          className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-[rgba(var(--border),0.35)]"
          title={linea.ocupacionPct !== null ? `Ocupación ${linea.ocupacionPct}% de la capacidad configurada` : 'La línea no tiene capacidad configurada; la barra compara contra la línea más cargada.'}
        >
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(width, 2)}%`, background: status.bar }} />
          {linea.ocupacionPct !== null ? <span className="absolute inset-y-0 w-0.5 bg-[rgba(var(--fg),0.45)]" style={{ left: `${LINE_READY_PCT}%` }} aria-hidden="true" /> : null}
        </div>
        <span className="w-12 shrink-0 text-right text-xs font-bold text-[rgb(var(--fg))]">{linea.ocupacionPct !== null ? `${linea.ocupacionPct}%` : '—'}</span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-[rgba(var(--fg),0.72)]">
        <span><strong className="text-[rgb(var(--fg))]">{formatNumber(linea.guias)}</strong> guías</span>
        <span><strong className="text-[rgb(var(--fg))]">{formatNumber(linea.bultos)}</strong> bultos</span>
        <span><strong className="text-[rgb(var(--fg))]">{formatNumber(linea.kg)}</strong>{linea.capacidad?.kg ? ` / ${formatNumber(linea.capacidad.kg)}` : ''} kg</span>
        <span><strong className="text-[rgb(var(--fg))]">{formatNumber(linea.m3)}</strong>{linea.capacidad?.m3 ? ` / ${formatNumber(linea.capacidad.m3)}` : ''} m³</span>
        <span title={formatCurrencyARS(linea.valor)}>VD <strong className="text-[rgb(var(--fg))]">{formatCompactCurrency(linea.valor)}</strong></span>
      </div>
    </button>
  );
}

function HourlyActivity({ items }) {
  const max = Math.max(1, ...items.map((item) => Math.max(item.hoy, item.ayer)));
  const totalHoy = items.reduce((acc, item) => acc + item.hoy, 0);
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[11px] text-[rgba(var(--fg),0.72)]">
        <span><strong className="text-[rgb(var(--fg))]">{formatNumber(totalHoy)}</strong> guías emitidas hoy</span>
        <span className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[rgb(var(--brand))]" />Hoy</span>
          <span className="inline-flex items-center gap-1"><span className="h-0.5 w-3 rounded bg-[rgba(var(--fg),0.5)]" />Ayer</span>
        </span>
      </div>
      <div className="flex h-28 items-end gap-[2px]">
        {items.map((item) => (
          <div
            key={item.hora}
            className="group relative flex h-full min-w-0 flex-1 flex-col justify-end"
            title={`${String(item.hora).padStart(2, '0')}:00 · hoy ${item.hoy} · ayer ${item.ayer}`}
            aria-label={`${item.hora} horas: hoy ${item.hoy}, ayer ${item.ayer}`}
          >
            <span className="absolute inset-x-0 h-0.5 rounded bg-[rgba(var(--fg),0.45)]" style={{ bottom: `${(item.ayer / max) * 100}%` }} aria-hidden="true" />
            <div className="rounded-t-[4px] bg-[rgb(var(--brand))] transition group-hover:brightness-110" style={{ height: `${(item.hoy / max) * 100}%`, minHeight: item.hoy ? 3 : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[9px] text-[rgba(var(--muted),0.9)]">
        <span>{`${String(items[0]?.hora ?? 0).padStart(2, '0')}h`}</span>
        <span>{`${String(items.at(-1)?.hora ?? 23).padStart(2, '0')}h`}</span>
      </div>
    </div>
  );
}

function SegmentBar({ segments }) {
  const total = segments.reduce((acc, item) => acc + item.value, 0);
  if (!total) return <div className="h-2.5 rounded-full bg-[rgba(var(--border),0.35)]" />;
  return (
    <div className="flex h-2.5 gap-[2px] overflow-hidden rounded-full">
      {segments.filter((item) => item.value > 0).map((item) => (
        <div key={item.label} title={`${item.label}: ${item.value}`} style={{ width: `${(item.value / total) * 100}%`, background: item.color }} />
      ))}
    </div>
  );
}

function StatLine({ label, value, color, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-2 rounded-lg px-1.5 py-1 text-left text-xs hover:bg-[rgba(var(--muted),0.08)]">
      <span className="inline-flex items-center gap-2 text-[rgba(var(--fg),0.78)]">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        {label}
      </span>
      <span className="font-bold text-[rgb(var(--fg))]">{value === null ? '—' : formatNumber(value)}</span>
    </button>
  );
}

const fojaDelayLabel = (foja) => {
  if (!foja.programada) return `Abierta hace ${formatDuration(foja.demoraMinutos)}`;
  if (foja.demoraMinutos > 0) return `Atrasada ${formatDuration(foja.demoraMinutos)}`;
  return `Sale en ${formatDuration(Math.abs(foja.demoraMinutos))}`;
};

const FOJA_ROW_TONES = {
  critica: 'border-[rgba(var(--danger),0.35)] bg-[rgba(var(--danger),0.06)]',
  alerta: 'border-[rgba(var(--warning),0.35)] bg-[rgba(var(--warning),0.06)]',
  normal: 'border-[rgba(var(--border),0.45)] bg-[rgba(var(--bg),0.3)]',
};

const buildFocus = (data, criticas, advertencias) => {
  if (!data) return [];
  const items = [];
  const atrasadas = (data.fojasSinSalida?.items || []).filter((foja) => foja.severidad !== 'normal').length;
  const listas = (data.lineas || []).filter((linea) => linea.estado === 'lista').length;
  if (criticas) items.push({ id: 'alertas', tone: 'critical', label: plural(criticas, 'alerta crítica', 'alertas críticas'), to: '/operaciones/alertas' });
  else if (advertencias) items.push({ id: 'advertencias', tone: 'warning', label: plural(advertencias, 'advertencia de trazabilidad', 'advertencias de trazabilidad'), to: '/operaciones/alertas' });
  if (atrasadas) items.push({ id: 'fojas', tone: 'critical', label: plural(atrasadas, 'foja atrasada', 'fojas atrasadas'), to: '/cargas/fojas-de-ruta' });
  if (data.retiros?.vencidos) items.push({ id: 'retiros', tone: 'critical', label: plural(data.retiros.vencidos, 'retiro vencido', 'retiros vencidos'), to: '/retiros' });
  if (data.repartos?.abiertosAnteriores) items.push({ id: 'repartos', tone: 'warning', label: plural(data.repartos.abiertosAnteriores, 'reparto de días anteriores sin cerrar', 'repartos de días anteriores sin cerrar'), to: '/cargas/repartos' });
  if (listas) items.push({ id: 'lineas', tone: 'info', label: plural(listas, 'línea lista para despachar', 'líneas listas para despachar'), to: '/cargas/fojas-de-ruta' });
  return items;
};

export default function DashboardCargas() {
  const navigate = useNavigate();
  const { sucursal, can } = useAuth();
  const sucursalId = Number(sucursal?.id || 0) || null;

  const fetcher = useCallback(() => fetchDashboardCargas({ sucursalId }), [sucursalId]);
  const { data, error, loading, updatedAt, reload } = useDashboardResource(fetcher, {
    refreshMs: REFRESH_MS,
    errorFallback: 'No se pudo cargar el tablero de cargas.',
  });

  const [{ data: alertasData }] = useQuery({
    query: ALERTAS_DASHBOARD,
    variables: { sucursal_id: sucursalId, limite: 100 },
    pause: !sucursalId,
  });
  const alertas = alertasData?.alertasDashboard || [];
  const criticas = alertas.filter((alerta) => alerta.severidad === 'CRIT').length;
  const advertencias = alertas.filter((alerta) => alerta.severidad === 'WARN').length;

  const focus = useMemo(() => buildFocus(data, criticas, advertencias), [data, criticas, advertencias]);
  const unavailable = new Set(data?.unavailable || []);
  const maxLineKg = Math.max(1, ...(data?.lineas || []).map((linea) => linea.kg));

  const description = `${data?.sucursal?.nombre || sucursal?.nombre || 'Sucursal activa'} · operación de hoy${updatedAt ? ` · actualizado ${timeLabel(updatedAt)}` : ''}`;

  return (
    <CargasPage
      title="Dashboard de cargas"
      description={description}
      hideHeaderOnCompact
      actions={(
        <button type="button" className={cargasButtonClassName} onClick={reload} disabled={loading}>
          <RefreshCw size={14} className={`mr-1 inline ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          {loading ? 'Actualizando…' : 'Actualizar'}
        </button>
      )}
    >
      <div className="flex h-full min-h-0 flex-col gap-2 overflow-y-auto overflow-x-hidden px-1 pb-2">
        <div className="flex flex-wrap items-center gap-2">
          {QUICK_ACTIONS.filter(({ permission }) => can?.(permission)).map(({ label, icon, to }) => { const Icon = icon; return (
            <button
              key={label}
              type="button"
              onClick={() => navigate(to)}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm transition hover:border-[rgba(var(--brand),0.4)] hover:bg-[rgba(var(--brand),0.08)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              <Icon size={14} className="text-[rgb(var(--brand))]" aria-hidden="true" />
              {label}
            </button>
          ); })}
        </div>

        {error ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[rgba(var(--danger),0.4)] bg-[rgba(var(--danger),0.08)] px-4 py-2 text-sm text-[rgb(var(--fg))]">
            <span className="inline-flex items-center gap-2"><AlertTriangle size={16} className="text-[rgb(var(--danger))]" aria-hidden="true" />{error}{data ? ' Se muestran los últimos datos obtenidos.' : ''}</span>
            <button type="button" onClick={reload} className={cargasButtonClassName}>Reintentar</button>
          </div>
        ) : null}

        {!data && loading ? (
          <div className="grid animate-pulse gap-2 sm:grid-cols-5" aria-busy="true">
            {Array.from({ length: 5 }, (_, index) => <div key={index} className="h-24 rounded-2xl bg-[rgba(var(--muted),0.14)]" />)}
            <div className="h-64 rounded-2xl bg-[rgba(var(--muted),0.12)] sm:col-span-5" />
          </div>
        ) : null}

        {data ? (
          <>
            {unavailable.size ? (
              <div className="flex items-center gap-2 rounded-xl border border-[rgba(var(--warning),0.4)] bg-[rgba(var(--warning),0.08)] px-4 py-2 text-xs text-[rgb(var(--fg))]">
                <AlertTriangle size={14} className="text-[rgb(var(--warning))]" aria-hidden="true" />
                {`No se pudieron calcular: ${[...unavailable].map((key) => SECTION_LABELS[key] || key).join(', ')}. El resto del tablero está actualizado.`}
              </div>
            ) : null}

            <FocusStrip items={focus} onNavigate={navigate} />

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
              <KpiCard testId="kpi-guias" unavailable={unavailable.has('kpis')} icon={FileText} label="Guías emitidas hoy" value={formatNumber(data.kpis.guias.hoy)} footer={<DeltaBadge deltaPct={data.kpis.guias.deltaPct} />} />
              <KpiCard testId="kpi-kg" unavailable={unavailable.has('kpis')} icon={Weight} label="Kilos hoy" value={formatNumber(data.kpis.kg.hoy)} footer={<DeltaBadge deltaPct={data.kpis.kg.deltaPct} />} />
              <KpiCard testId="kpi-m3" unavailable={unavailable.has('kpis')} icon={Boxes} label="M³ hoy" value={formatNumber(data.kpis.m3.hoy)} footer={<DeltaBadge deltaPct={data.kpis.m3.deltaPct} />} />
              <KpiCard testId="kpi-importe" unavailable={unavailable.has('kpis')} icon={CircleDollarSign} label="Facturable hoy" value={formatCompactCurrency(data.kpis.importe.hoy)} title={formatCurrencyARS(data.kpis.importe.hoy)} footer={<DeltaBadge deltaPct={data.kpis.importe.deltaPct} />} />
              <KpiCard
                testId="kpi-por-recibir"
                unavailable={unavailable.has('porRecibir')}
                icon={Route}
                label="En camino a la sucursal"
                value={formatNumber(data.kpis.porRecibir.guias)}
                footer={<span className="text-[11px] text-[rgba(var(--fg),0.72)]">{`${formatNumber(data.kpis.porRecibir.bultos)} bultos · ${formatNumber(data.kpis.porRecibir.kg)} kg`}</span>}
              />
            </div>

            <div className="grid grid-cols-12 gap-2">
              <Region title="Carga pendiente por línea" className="col-span-12 xl:col-span-8">
                <CargasSection
                  title="Carga pendiente por línea"
                  description={`Guías emitidas o en foja que todavía no salieron, contra la capacidad de cada línea (marca al ${LINE_READY_PCT}%).`}
                  className="h-full"
                  bodyClassName="space-y-2 p-3"
                  compact
                >
                  {unavailable.has('lineas') ? <Unavailable /> : data.lineas.length === 0 ? (
                    <Empty>No hay carga pendiente de despacho. Todo lo emitido ya salió.</Empty>
                  ) : data.lineas.map((linea) => (
                    <LineRow key={linea.linea} linea={linea} maxKg={maxLineKg} onOpen={() => navigate('/cargas/fojas-de-ruta')} />
                  ))}
                </CargasSection>
              </Region>

              <div className="col-span-12 flex flex-col gap-2 xl:col-span-4">
                <Region title="Ritmo de emisión">
                  <CargasSection title="Ritmo de emisión" description="Guías por hora, hoy contra ayer." bodyClassName="p-3" compact>
                    {unavailable.has('actividad') ? <Unavailable /> : <HourlyActivity items={data.actividadPorHora} />}
                  </CargasSection>
                </Region>

                <Region title="Repartos del día">
                  <CargasSection
                    title="Repartos del día"
                    bodyClassName="space-y-2 p-3"
                    compact
                    actions={<span className="text-xs font-bold text-[rgb(var(--fg))]">{formatNumber(data.repartos.total)}</span>}
                  >
                    {unavailable.has('repartos') ? <Unavailable /> : (
                      <>
                        <SegmentBar segments={[
                          { label: 'Finalizados', value: data.repartos.finalizados, color: 'rgb(var(--success))' },
                          { label: 'En curso', value: data.repartos.enCurso, color: 'rgb(var(--brand))' },
                          { label: 'Pendientes', value: data.repartos.pendientes, color: 'rgb(var(--warning))' },
                        ]}
                        />
                        <div>
                          <StatLine label="Finalizados" value={data.repartos.finalizados} color="rgb(var(--success))" onClick={() => navigate('/cargas/repartos')} />
                          <StatLine label="En curso" value={data.repartos.enCurso} color="rgb(var(--brand))" onClick={() => navigate('/cargas/repartos')} />
                          <StatLine label="Pendientes de salir" value={data.repartos.pendientes} color="rgb(var(--warning))" onClick={() => navigate('/cargas/repartos')} />
                        </div>
                      </>
                    )}
                  </CargasSection>
                </Region>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2">
              <Region title="Fojas sin salida" className="col-span-12 lg:col-span-6">
                <CargasSection
                  title="Fojas sin salida"
                  description="Ordenadas por salida programada. Rojo: más de 1 h de atraso."
                  className="h-full"
                  bodyClassName="space-y-1.5 p-2"
                  compact
                  actions={data.fojasSinSalida.total ? <span className="rounded-full bg-[rgba(var(--muted),0.14)] px-2 py-0.5 text-[10px] font-bold text-[rgb(var(--fg))]">{formatNumber(data.fojasSinSalida.total)}</span> : null}
                >
                  {unavailable.has('fojas') ? <Unavailable /> : data.fojasSinSalida.items.length === 0 ? (
                    <Empty>Todas las fojas salieron. Sin pendientes de despacho.</Empty>
                  ) : data.fojasSinSalida.items.slice(0, 8).map((foja) => (
                    <button
                      key={foja.id}
                      type="button"
                      onClick={() => navigate(`/cargas/fojas-de-ruta?id=${foja.id}`)}
                      className={`grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-2 rounded-xl border px-3 py-2 text-left transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${FOJA_ROW_TONES[foja.severidad] || FOJA_ROW_TONES.normal}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-mono text-xs font-bold text-[rgb(var(--fg))]">{foja.numero}</span>
                        <span className="block truncate text-[10px] text-[rgba(var(--fg),0.65)]">{`${foja.destino} · ${formatNumber(foja.guias)} guías`}</span>
                      </span>
                      <span className="truncate text-xs text-[rgb(var(--fg))]">{foja.chofer}</span>
                      <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-[rgb(var(--fg))]">
                        {foja.severidad === 'normal'
                          ? <Clock3 size={12} aria-hidden="true" />
                          : <AlertTriangle size={12} className={foja.severidad === 'critica' ? 'text-[rgb(var(--danger))]' : 'text-[rgb(var(--warning))]'} aria-hidden="true" />}
                        {fojaDelayLabel(foja)}
                      </span>
                    </button>
                  ))}
                </CargasSection>
              </Region>

              <Region title="Entregas y retiros" className="col-span-12 md:col-span-6 lg:col-span-3">
                <CargasSection title="Entregas y retiros" className="h-full" bodyClassName="space-y-3 p-3" compact>
                  {unavailable.has('entregas') ? <Unavailable /> : (
                    <div>
                      <div className="flex items-end justify-between">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(var(--muted),0.9)]">Efectividad de entrega</span>
                        <span className="text-xl font-bold text-[rgb(var(--fg))]">{data.entregas.efectividadPct === null ? '—' : `${data.entregas.efectividadPct}%`}</span>
                      </div>
                      <SegmentBar segments={[
                        { label: 'Entregadas', value: data.entregas.entregadas, color: 'rgb(var(--success))' },
                        { label: 'Pendientes', value: data.entregas.pendientes, color: 'rgba(var(--muted),0.55)' },
                        { label: 'No entregadas', value: data.entregas.fallidas, color: 'rgb(var(--danger))' },
                      ]}
                      />
                      <StatLine label="Entregadas" value={data.entregas.entregadas} color="rgb(var(--success))" onClick={() => navigate('/cargas/repartos')} />
                      <StatLine label="En calle" value={data.entregas.pendientes} color="rgba(var(--muted),0.55)" onClick={() => navigate('/cargas/repartos')} />
                      <StatLine label="No entregadas" value={data.entregas.fallidas} color="rgb(var(--danger))" onClick={() => navigate('/cargas/novedades')} />
                      <StatLine label="Listas para repartir" value={unavailable.has('colaReparto') ? null : data.entregas.colaReparto.guias} color="rgb(var(--brand))" onClick={() => navigate('/cargas/repartos/nuevo')} />
                    </div>
                  )}
                  <div className="border-t border-[rgba(var(--border),0.45)] pt-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[rgba(var(--muted),0.9)]">Retiros</span>
                    {unavailable.has('retiros') ? <Unavailable /> : (
                      <>
                        <StatLine label="Pendientes" value={data.retiros.pendientes} color="rgb(var(--warning))" onClick={() => navigate('/retiros')} />
                        <StatLine label="Vencidos" value={data.retiros.vencidos} color="rgb(var(--danger))" onClick={() => navigate('/retiros')} />
                        <StatLine label="Realizados hoy" value={data.retiros.realizadosHoy} color="rgb(var(--success))" onClick={() => navigate('/retiros')} />
                      </>
                    )}
                  </div>
                </CargasSection>
              </Region>

              <Region title="Destinos de hoy" className="col-span-12 md:col-span-6 lg:col-span-3">
                <CargasSection
                  title="Destinos de hoy"
                  className="h-full"
                  bodyClassName="space-y-2 p-3"
                  compact
                >
                  {unavailable.has('destinos') ? <Unavailable /> : data.guiasPorDestino.length === 0 ? (
                    <Empty>Todavía no se emitieron guías hoy.</Empty>
                  ) : data.guiasPorDestino.slice(0, 7).map((destino) => (
                    <div key={destino.id ?? 'sin-destino'} title={`${destino.nombre}: ${destino.guias} guías · ${formatNumber(destino.kg)} kg`}>
                      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                        <span className="truncate font-semibold text-[rgb(var(--fg))]">{destino.nombre}</span>
                        <span className="shrink-0 text-[rgba(var(--fg),0.72)]"><strong className="text-[rgb(var(--fg))]">{formatNumber(destino.guias)}</strong>{` · ${destino.pct}%`}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[rgba(var(--border),0.3)]">
                        <div className="h-2 rounded-full bg-[rgb(var(--brand))]" style={{ width: `${Math.max(destino.pct, 2)}%` }} />
                      </div>
                    </div>
                  ))}
                </CargasSection>
              </Region>
            </div>
          </>
        ) : null}
      </div>
    </CargasPage>
  );
}

